import React from 'react';
import { GRID_CHECKBOX_SELECTION_COL_DEF, GridColDef } from '@mui/x-data-grid';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { colors } from 'common/constants';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { ICustomer } from 'types/customer';
import CustomerStatus from './customer-status';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';

const CustomerGridCollumn = (
    onClickDeleteRow: (row: ICustomer) => void,
    onClickEditRow: (row: ICustomer) => void,
    navigateToDetail: (row: ICustomer) => void
) => {
    const columnsDefinitions: GridColDef[] = [
        {
            ...GRID_CHECKBOX_SELECTION_COL_DEF,
            width: 45,
        },
        {
            field: 'name',
            headerName: 'Họ và tên',
            flex: 1,
            renderCell: (params) => (
                <Box
                    component="div"
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    height="100%"
                >
                    <Tooltip title={params.row.username} placement="top" arrow>
                        <Typography
                            component="strong"
                            fontWeight={600}
                            fontSize="14px"
                            lineHeight="17px"
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '100%',
                                cursor: 'pointer',
                            }}
                        >
                            {params.row.username}
                        </Typography>
                    </Tooltip>

                    <Tooltip title={params.row.email} placement="top" arrow>
                        <Typography
                            component="span"
                            fontSize="14px"
                            lineHeight="17px"
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '100%',
                                cursor: 'pointer',
                            }}
                        >
                            {params.row.email}
                        </Typography>
                    </Tooltip>
                </Box>
            ),
        },
        {
            field: 'packageService',
            headerName: 'Gói dịch vụ',
            flex: 1,
            sortable: true,
            renderCell: (params) => (
                <Box
                    component="div"
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    height={'100%'}
                    sx={{
                        textWrap: 'wrap',
                        paddingRight: '15px',
                    }}
                >
                    <Typography
                        component="strong"
                        fontSize={'14px'}
                        lineHeight={'17px'}
                    >
                        {params.row.plan.name}
                        {params.row.plan.is_expiring_soon && (
                            <span style={{ color: 'red' }}>(Sắp hết hạn)</span>
                        )}
                    </Typography>
                    <Typography
                        component="strong"
                        fontSize={'14px'}
                        lineHeight={'17px'}
                    >
                        {params.row.plan.start_date} -{' '}
                        {params.row.plan.end_date}
                    </Typography>
                </Box>
            ),
        },
        {
            field: 'campaign',
            headerName: 'Chiến dịch',
            flex: 1,
            sortable: true,
            renderCell: (params) => (
                <Box
                    component="div"
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    height={'100%'}
                    sx={{
                        textWrap: 'wrap',
                        paddingRight: '15px',
                    }}
                >
                    <Typography fontSize={'14px'} lineHeight={'17px'}>
                        Dịch vụ khảo sát:
                        {params.row.survey_config.paid}
                    </Typography>
                    <Typography fontSize={'14px'} lineHeight={'17px'}>
                        Chiến dịch miễn phí: {params.row.survey_config.free}
                    </Typography>
                </Box>
            ),
        },
        {
            field: 'totalPaid',
            headerName: 'Thanh toán',
            flex: 1,
            renderCell: (params) => (
                <Box
                    component="div"
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    height={'100%'}
                    sx={{
                        textWrap: 'wrap',
                        paddingRight: '15px',
                    }}
                >
                    <Typography fontSize={'14px'} lineHeight={'17px'}>
                        Tổng thanh toán: {params.row.payment.count}
                    </Typography>
                    <Typography fontSize={'14px'} lineHeight={'17px'}>
                        Tổng tiền: {params.row.payment.money}
                    </Typography>
                </Box>
            ),
        },
        {
            field: 'status',
            headerName: 'Trạng thái',
            flex: 1,
            renderCell: (params) => (
                <Box
                    component="div"
                    display="flex"
                    gap={1}
                    alignItems="center"
                    height={'100%'}
                >
                    <CustomerStatus status={params.row.status} />
                </Box>
            ),
        },
        {
            field: 'source',
            headerName: 'Nguồn',
            flex: 1,
            renderCell: (params) => (
                <Box component="div">
                    <Typography
                        component="span"
                        fontSize={'14px'}
                        lineHeight={'17px'}
                    >
                        {params.row.created_by}
                    </Typography>
                </Box>
            ),
        },
        {
            field: 'activationDate',
            headerName: 'Ngày kích hoạt',
            flex: 1,
            renderCell: (params) => (
                <Box component="div">
                    <Typography
                        component="span"
                        fontSize={'14px'}
                        lineHeight={'17px'}
                    >
                        {params.row.created_at}
                    </Typography>
                </Box>
            ),
        },

        {
            field: 'actions',
            headerName: 'Thao tác',
            sortable: false,
            flex: 1,
            renderCell: (params) => (
                <Box
                    component="div"
                    display="flex"
                    alignItems="center"
                    height={'100%'}
                >
                    <IconButton onClick={() => navigateToDetail(params.row)}>
                        <RemoveRedEyeOutlinedIcon sx={{ color: colors.main }} />
                    </IconButton>
                    {params.row.is_delete && (
                        <IconButton
                            onClick={() => onClickDeleteRow(params.row)}
                        >
                            <DeleteOutlineOutlinedIcon
                                sx={{ color: colors.main }}
                            />
                        </IconButton>
                    )}
                    <IconButton onClick={() => onClickEditRow(params.row)}>
                        <EditOutlinedIcon sx={{ color: colors.main }} />
                    </IconButton>
                </Box>
            ),
        },
    ];
    return columnsDefinitions;
};

export default CustomerGridCollumn;
